import * as d3 from 'd3';
import {DateTime, DateTimeUnit, Interval} from 'luxon';
import React from 'react';

type LabelProps = {
  graphDims: [number, number];
  text: string;
  isFlipped?: boolean;
};

type AxisProps = {
  axisCreator: d3.Axis<any>;
  yLabelProps?: LabelProps;
  className?: string;
  transform?: string;
};

export type XScale = d3.ScaleTime<number, number, never>;
export type YScale = d3.ScaleLinear<number, number, never>;

function AxisLabel(props: LabelProps) {
  const flipper = props.isFlipped ? 1 : -1;
  return (
    <g transform={`rotate(${flipper * 90})`}>
      <text
        textAnchor="middle"
        dominantBaseline="central"
        x={(flipper * props.graphDims[1]) / 2}
        dy={-48}
        fontSize={12}
        className="fill-current"
      >
        {props.text}
      </text>
    </g>
  );
}

export function getDateTickOptions(startDate: Date, endDate: Date, width: number) {
  const daysDiff = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
  let dateTimeUnit, countBetweenTicks, numTicks;
  if (daysDiff < 60) {
    numTicks = Math.min(width / 50, 20);
    dateTimeUnit = 'day';
    countBetweenTicks = Math.ceil(daysDiff / numTicks);
  } else {
    numTicks = Math.min(width / 80);
    dateTimeUnit = 'month';
    countBetweenTicks = Math.ceil(daysDiff / 30 / numTicks);
  }
  return {
    numTicks,
    dateTimeUnit: dateTimeUnit as DateTimeUnit,
    countBetweenTicks,
  };
}

export function getDateTicks(
  startDate: Date,
  endDate: Date,
  timeZone: string,
  width: number
): Date[] {
  const {dateTimeUnit, countBetweenTicks} = getDateTickOptions(startDate, endDate, width);

  const startDateTime = DateTime.fromJSDate(startDate, {zone: timeZone});
  const endDateTime = DateTime.fromJSDate(endDate, {zone: timeZone});
  const interval = Interval.fromDateTimes(startDateTime, endDateTime);

  return Array.from(startOfUnitDates(interval, dateTimeUnit, countBetweenTicks)).filter(
    (d) => startDate <= d && d <= endDate
  );
}

export function formatDateTick(date: Date, dateTimeUnit: DateTimeUnit, timeZone: string) {
  if (dateTimeUnit === 'day') {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      timeZone,
    }).format(date);
  } else if (dateTimeUnit === 'month') {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      timeZone,
    }).format(date);
  } else {
    throw Error(`Unsupported tick dateTimeUnit ${dateTimeUnit}`);
  }
}

export function formatDayDateTick(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    timeZone,
  }).format(date);
}

export function formatMonthDateTick(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    timeZone,
  }).format(date);
}

function* startOfUnitDates(interval: Interval, dateTimeUnit: DateTimeUnit, jump: number) {
  if (!interval.start || !interval.end) {
    return;
  }

  let cursor = interval.start.startOf(dateTimeUnit);
  while (cursor < interval.end) {
    yield cursor.toJSDate();
    cursor = cursor.plus({[dateTimeUnit]: jump});
  }
}

// useful for days not equal to one, where you might have weird overlap over the
// first day of the month (which d3 force-renders) and other days
export function ticksEvery(days: number) {
  return d3.timeDay.filter((d) => d3.timeDay.count(new Date(), d) % days === 0);
}

const ReallySmallNumberFormatter = d3.format('.3f');
const SmallNumberFormatter = d3.format('.2f');
const MediumNumberFormatter = d3.format('.1f');
const SIFormatter = d3.format('.2s');

export function defaultNumericTickFormat(value: number): string {
  if (value == 0) {
    return '0';
  } else if (value < 0.1 && value > -0.1) {
    return ReallySmallNumberFormatter(value);
  } else if (value < 1 && value > -1) {
    return SmallNumberFormatter(value);
  } else if (value > 999999999) {
    return SIFormatter(value).replace(/G/, 'B');
  } else {
    return SIFormatter(value);
  }
}

export function defaultTooltipFormat(value: number): string {
  if (value < 0.1 && value > -0.1) {
    // Values typically in the precip range. Really tiny. (e.g. .001 mm/hr)
    return ReallySmallNumberFormatter(value);
  } else if (value < 10 && value > -10) {
    return SmallNumberFormatter(value);
  } else if (value < 100 && value > -100) {
    return MediumNumberFormatter(value);
  } else {
    // locale string gives us a nice comma (or locally) separated numbers
    return Math.round(value as number).toLocaleString();
  }
}

export default function Axis(props: AxisProps) {
  const axisRef = (axis: SVGGElement) => {
    axis && props.axisCreator(d3.select(axis));
  };

  return (
    <g className="text-gray-500" transform={props.transform}>
      <g className={props.className} ref={axisRef}></g>
      {props.yLabelProps && <AxisLabel {...props.yLabelProps} />}
    </g>
  );
}
